import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Brain, Package, Shield, ShoppingCart, Truck, Plane,
  Building, Landmark, Sprout, TrendingUp, BarChart3,
  Target, Zap, Users, Eye, ArrowRight, Sparkles
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import aiDevCover from '../assets/cover/ai-development-cover.png';
import aiDevLifecycle from '../assets/ai-dev-lifecycle.png';

const AIDevelopment = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const aiServices = [
    {
      icon: Sparkles,
      title: 'Generative AI',
      description: 'We design generative AI models tailored to your business — enabling automated content creation, intelligent recommendations, and conversational experiences.'
    },
    {
      icon: Package,
      title: 'AI Product Development',
      description: 'We build end-to-end AI-powered products — from data strategy and model training to platform deployment — accelerating your time to market.'
    },
    {
      icon: Shield,
      title: 'AI Security',
      description: 'Security-first AI design ensuring model integrity, compliance, and ethical assurance. We embed responsible AI principles and risk control into every layer.'
    }
  ];

  const industries = [
    { icon: ShoppingCart, name: 'E-commerce' },
    { icon: Truck, name: 'Logistics' },
    { icon: Plane, name: 'Aviation' },
    { icon: Building, name: 'Real Estate' },
    { icon: Landmark, name: 'Government' },
    { icon: Sprout, name: 'Agriculture' }
  ];

  const processSteps = [
    'AI Strategy & Planning',
    'Data Collection & Preparation',
    'Algorithm Design',
    'Model Training & Tuning',
    'Validation & Optimization',
    'Deployment & Integration'
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Enhanced Forecasting Accuracy',
      description: 'Predict trends and future outcomes using intelligent forecasting models that evolve with your data.'
    },
    {
      icon: BarChart3,
      title: 'Effective Data Analytics',
      description: 'Transform raw, unstructured data into actionable insights through automated AI data analysis.'
    },
    {
      icon: Target,
      title: 'Better Decision-Making',
      description: 'Empower leadership with real-time intelligence and predictive dashboards that enhance decision accuracy.'
    },
    {
      icon: Zap,
      title: 'Workflow Automation',
      description: 'Automate repetitive tasks with AI-driven workflows that boost efficiency and reduce errors.'
    },
    {
      icon: TrendingUp,
      title: 'Improved Productivity',
      description: 'Deploy AI tools and dashboards that maximize performance and resource allocation.'
    },
    {
      icon: Users,
      title: 'Better Customer Experience',
      description: 'Deliver personalized, adaptive, and smart customer journeys through AI-driven experiences.'
    }
  ];

  const aiModels = [
    {
      name: 'GPT-4o',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1745724910146-light-tihzikgk15b.svg',
      alt: 'OpenAI GPT-4o Logo'
    },
    {
      name: 'Llama 3',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1745111214173-v9clzccijvn.svg',
      alt: 'Meta Llama 3 Logo'
    },
    {
      name: 'PaLM 2',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1745196514990-v5xo5qar19.svg',
      alt: 'Google PaLM 2 Logo'
    },
    {
      name: 'Stability AI',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1745040549692-a8t8bfqnua7.svg',
      alt: 'Stability AI Logo'
    },
    {
      name: 'Google Gemini',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1745196514990-v5xo5qar19.svg',
      alt: 'Google Gemini Logo'
    },
    {
      name: 'Vicuna',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1746056004804-light-5efxpqkf09g.svg',
      alt: 'Vicuna AI Logo'
    },
    {
      name: 'Mistral',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1746331059628-light-v5blniuo3i.svg',
      alt: 'Mistral AI Logo'
    },
    {
      name: 'Claude',
      logo: 'https://ngccgynpmxypswzjxnpx.supabase.co/storage/v1/object/public/logo-submissions/logos/1745537600455-light-l9ths514lg.svg',
      alt: 'Anthropic Claude Logo'
    }
  ];

  const whyChoose = [
    {
      icon: Brain,
      title: 'Full-Cycle AI Expertise',
      description: 'We manage every step of the AI lifecycle — from data strategy and model design to testing, optimization, and deployment.'
    },
    {
      icon: Shield,
      title: 'Responsible AI Practices',
      description: 'Our AI systems are guided by fairness, transparency, and security — ensuring compliance with ethical AI principles.'
    },
    {
      icon: Eye,
      title: 'Focus on Data Quality & Integrity',
      description: 'We maintain clean, structured, and verified data pipelines for accurate, bias-free outcomes.'
    }
  ];

  return (
    <div className="bg-gradient-to-b from-gray-50 via-white to-gray-50 text-gray-900 overflow-hidden relative">
      <Navbar />

      {/* Hero Section */}
      <section className="min-h-[50vh] flex items-center justify-center relative px-6 lg:px-12 pt-24 pb-12 overflow-hidden">
        <img
          src={aiDevCover}
          alt="AI Development cover"
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-100 via-white to-gray-50"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-8 leading-[1.1] text-gray-900 animate-fade-in" style={{ fontWeight: 300 }}>
            AI Development
            <br />
            <span className="text-blue-600">Services</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed font-light animate-fade-in" style={{ animationDelay: '200ms' }}>
            We create intelligent, adaptive, and future-ready AI solutions that empower businesses to evolve faster.
            Our strategic, secure, and scalable AI services turn innovation into measurable results.
          </p>
          <div className="relative inline-block group animate-fade-in" style={{ animationDelay: '400ms' }}>
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity duration-500"></div>
            <Button
              size="lg"
              className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-8 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <span className="relative z-10">Get Free Consultation</span>
              <div className="absolute inset-0 rounded-xl overflow-hidden">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                What is
                <br />
                <span className="text-blue-600">AI Development?</span>
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                <p>
                  AI development is the process of building intelligent systems that can analyze, learn, and adapt — mimicking human reasoning to deliver real business value.
                  At ThinkVerse.AI, we design, train, and deploy advanced AI models and intelligent software that solve complex problems through automation, data analysis, and prediction.
                </p>
                <p>
                  From chatbots and generative models to autonomous decision engines — our AI systems evolve with your business. We blend innovation, ethics, and engineering precision to help you stay ahead of tomorrow.
                </p>
              </div>
            </div>

            <div className="scroll-reveal opacity-0 flex items-center justify-center">
              <div className="relative w-full max-w-xl aspect-[16/10]">
                <img
                  src={aiDevLifecycle}
                  alt="AI Development lifecycle"
                  className="relative w-full h-full object-contain rounded-2xl shadow-2xl bg-transparent transform transition duration-500 ease-out hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Services Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-sky-100/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Build AI That Moves Your
              <br />
              <span className="text-blue-600">Business Forward</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Our AI engineers deliver reliable, ethical, and high-performance AI solutions that generate measurable business outcomes.
              By combining innovation with responsibility, we build AI systems that adapt intelligently and act purposefully.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {aiServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border border-gray-200 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:shadow-2xl scroll-reveal opacity-0 group relative overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-sky-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <CardContent className="p-8 relative">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-sky-500 rounded-full blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                      <div className="relative text-blue-600 group-hover:scale-110 transition-transform duration-300">
                        <Icon size={48} />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>

                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center scroll-reveal opacity-0">
            <div className="relative inline-block group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
              <Button
                size="lg"
                className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-8 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <span className="relative z-10 flex items-center">
                  Consult Our AI Experts <ArrowRight className="ml-2" size={20} />
                </span>
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Offering Industry-Specific
              <br />
              <span className="text-blue-600">AI Solutions</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We design customized AI systems that address real-world challenges unique to your industry.
              Our AI solutions optimize operations, unlock insights, and scale intelligently.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className="relative group flex flex-col items-center justify-center p-6 bg-white rounded-lg border border-gray-200 hover:border-blue-500/50 transition-all duration-300 hover:scale-105 scroll-reveal opacity-0"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-sky-500/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur"></div>
                  <div className="relative">
                    <Icon size={32} className="text-blue-600 mb-3" />
                    <span className="text-gray-700 text-sm text-center block">{industry.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Development Process Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Our Result-Driven
              <br />
              <span className="text-blue-600">AI Development Process</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              At ThinkVerse.AI, every AI solution follows a strategic and transparent pipeline — from planning to deployment —
              ensuring quality, security, and scalability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="relative group p-8 bg-white border border-gray-200 rounded-lg hover:border-blue-500/50 transition-all duration-500 hover:shadow-lg scroll-reveal opacity-0"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/10 to-sky-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-4 left-4 text-blue-500/20 text-5xl font-bold group-hover:text-blue-500/30 transition-colors duration-300">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="relative mt-12">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">{step}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-100/30 to-sky-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10 scroll-reveal opacity-0">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Need a Custom
            <br />
            <span className="text-blue-600">AI Development Partner?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            Whether you're building custom models, predictive engines, or autonomous assistants — we'll help you bring intelligence to life.
            Together, let's build products that think.
          </p>
          <div className="relative inline-block group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-40 group-hover:opacity-70 transition-opacity duration-500"></div>
            <Button
              size="lg"
              className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-10 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <span className="relative z-10">Let's Build Your AI Product</span>
              <div className="absolute inset-0 rounded-xl overflow-hidden">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Business Benefits Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-100/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              How Your Business Benefits from
              <br />
              <span className="text-blue-600">Our AI Solutions</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Unlock efficiency, accuracy, and insight through AI-powered intelligence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card
                  key={index}
                  className="bg-white border border-gray-200 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:shadow-2xl scroll-reveal opacity-0 group relative overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-sky-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <CardContent className="p-8 relative">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-sky-500 rounded-full blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                      <div className="relative text-blue-600">
                        <Icon size={40} />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>

                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Models Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              AI Models We Have
              <br />
              <span className="text-blue-600">Expertise In</span>
            </h2>
          </div>

          <div className="flex overflow-x-auto gap-6 pb-8 scroll-reveal opacity-0" style={{ scrollbarWidth: 'thin' }}>
            {aiModels.map((model, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-64 h-40 p-8 bg-white border border-gray-200 rounded-lg hover:border-blue-500/50 transition-all duration-300 hover:scale-105 cursor-pointer flex items-center justify-center group relative"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/10 to-sky-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img
                  src={model.logo}
                  alt={model.alt}
                  className="relative max-w-full max-h-full object-contain filter brightness-0 invert opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<h3 class="text-2xl font-bold text-gray-900 text-center">${model.name}</h3>`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Why Choose ThinkVerse.AI for
              <br />
              <span className="text-blue-600">AI Development?</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We bring full-cycle expertise, ethical AI principles, and precision data handling to every solution we build.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="scroll-reveal opacity-0 p-8 bg-white border border-gray-200 rounded-lg hover:border-blue-500/50 transition-all duration-500 hover:shadow-lg group relative"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/10 to-sky-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-sky-500 rounded-full blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                    <div className="relative">
                      <Icon size={40} className="text-blue-600" />
                    </div>
                  </div>
                  <h3 className="relative text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="relative text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIDevelopment;
